/**
 * emotional — три отдельные карточки, зеркалящие EmotionalSection.tsx один в
 * один: «Анализ тональности» (только клиент, `clientStart → clientEnd`),
 * «Вежливость» (балл + нарушения) и «Чёткость» (балл + проблемы). У каждой
 * свой заголовок и свой гейт видимости — как на экране, где это три
 * отдельных `<AnalysisSection>`, а не один блок.
 *
 * Согласованный прототип (preview.html:296-303) сначала рисовал их одной
 * картой «Эмоции и вежливость» — это была ошибка эскиза вёрстки, не решение
 * о структуре: под такой заголовок не нашлось ни одного i18n-ключа ни в
 * одной локали (три экранных заголовка существуют, а «Эмоции и вежливость»
 * целиком — нет), и командир задачи подтвердил печатать тремя карточками
 * экрана вместо одной карточки прототипа.
 *
 * `sentiment.delta`/`overall` НЕ печатаются — бриф задачи 9 просит только
 * `clientStart → clientEnd`, хотя экран показывает и то, и другое (третий
 * блок под тем же `AnalysisSection`). Это единственное сознательное сужение
 * контента, оставленное в силе явным «остальное по брифу без изменений».
 *
 * Перебивания (`emotional.interruptions`) печатаются в `speech.ts`
 * («Анализ речи»), НЕ здесь. Первая версия этого файла клала их в карточку
 * «Вежливость» по прямому указанию — указание оказалось основано на
 * ошибочном предположении о том, где их сейчас показывает экран v2:
 * реальный владелец блока — `SpeechSection.tsx:77,118-138`, `EmotionalSection.
 * tsx` перебиваний не показывает вовсе. После проверки экрана построчно
 * решение исправлено, см. `speech.ts`. Благодаря переносу гейт карточки
 * «Вежливость» снова простой — печатается тогда и только тогда, когда есть
 * `politeness`, ровно как на экране.
 *
 * Эмоция оператора НЕ печатается ни при каких условиях (EU AI Act, ст.
 * 5(1)(f)) — и структурно не может: `sentiment` в типе (`analysis/types.ts`)
 * несёт только `clientStart`/`clientEnd`/`delta`/`overall`, поля агента там
 * нет вовсе, `politeness`/`clarity` эмоции не касаются.
 */
import { card, kvRow, scoreSpan } from '../primitives';
import { escapeHtml } from '../escape';
import type { SectionFn } from '../types';
import { sentimentLabel } from '@/components/details/v2/analysis/analysis-utils';

export const renderEmotional: SectionFn = (input, t) => {
  const emotional = input.analysis.result.emotional;
  const sentiment = emotional?.sentiment;
  const politeness = emotional?.politeness;
  const clarity = emotional?.clarity;

  const sentimentHtml = sentiment
    ? card({
        title: t('records.analysisPanel.sentimentAnalysis'),
        body: kvRow(
          t('records.analysisPanel.client'),
          `${escapeHtml(sentimentLabel(sentiment.clientStart, t))} → ${escapeHtml(
            sentimentLabel(sentiment.clientEnd, t)
          )}`
        ),
      })
    : '';

  const violationsHtml =
    politeness?.violations && politeness.violations.length > 0
      ? `<ul>${politeness.violations.map((v) => `<li>${escapeHtml(v)}</li>`).join('')}</ul>`
      : '';

  const politenessHtml = politeness
    ? card({
        title: t('records.analysisPanel.politeness'),
        value: scoreSpan(politeness.score),
        body: violationsHtml,
      })
    : '';

  const clarityIssuesHtml =
    clarity?.issues && clarity.issues.length > 0
      ? `<ul>${clarity.issues.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`
      : '';
  const clarityHtml = clarity
    ? card({
        title: t('records.analysisPanel.clarity'),
        value: clarity.score !== undefined ? scoreSpan(clarity.score) : undefined,
        body: clarityIssuesHtml,
      })
    : '';

  return sentimentHtml + politenessHtml + clarityHtml;
};
